// src/utils/agentBookings.js
// Partner dashboard data, built from the booking history intents that already exist:
//   flights -> intent "bookinghistory"       (same payload as services/flightBookingAPI.getBookingHistory)
//   buses   -> intent "bus_booking_history"  (same payload as pages/BookingHistory.fetchBusBookings)
// Both are called as the partner (From = webbot_agent:<email>, partner token attached), so the
// backend returns only that partner's bookings. Each booking may carry `net_agent_commission`
// (defaults to 0 when missing). Aggregates are computed here, in the browser.
//
// Fine for hundreds of bookings. When partners reach thousands, move aggregation to a backend
// intent (agent_dashboard) and keep normalizeFlight/normalizeBus as the shared definitions.

import { FEATURES } from "../config/features";
import { getAgentSession } from "./agentAuth";

const BASE = "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";
// Same environment switches the customer booking pages use
const FLIGHT_URL = FEATURES?.FLIGHT_TEST ? `${BASE}/development` : BASE;
const BUS_URL = FEATURES?.BUS_TEST ? `${BASE}/development` : BASE;
const END_USER_IP = "125.63.76.234";
const PAGE_SIZE = 100;
const MAX_PAGES = 10; // up to 1000 bookings per type

// ── status buckets ───────────────────────────────────────────────────────────
// Abandoned checkouts never show up (same rule as the customer BookingHistory page).
const HIDDEN = new Set(["INITIATED", "PAYMENT_PENDING", "ORDER_CREATING", "ORDER_FAILED", "QUOTED", "PAYMENT_FAILED"]);
const CONFIRMED = new Set(["TICKET_ISSUED", "SUCCESS"]);
const CANCELLED = new Set([
  "CANCELLED", "CANCELLATION_IN_PROGRESS", "REFUND_REQUESTED", "REFUND_IN_PROGRESS", "REFUND_COMPLETED", "REFUND_FAILED",
]);

export function bucketOf(status) {
  const s = String(status || "").toUpperCase();
  if (CONFIRMED.has(s)) return "confirmed";
  if (CANCELLED.has(s)) return "cancelled";
  if (s.includes("FAILED")) return "failed";
  return "processing"; // e.g. PAYMENT_SUCCESS: paid, ticket not issued yet
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const parseJson = (d) => {
  if (!d) return {};
  if (typeof d === "object") return d;
  try {
    const p = JSON.parse(d);
    return typeof p === "string" ? JSON.parse(p) : p;
  } catch {
    return {};
  }
};

// ── normalizers: one shape for both modes ────────────────────────────────────
// { id, type, status, bucket, source, destination, journey_date, created_at, pnr, pax, amount, commission }

/** Same fare rule as BookingRow in pages/BookingHistory.js */
export function flightFare(b, ticketDetails) {
  if (b?.total_final_fare_with_taxes_and_discounts) return num(b.total_final_fare_with_taxes_and_discounts);
  const published = num(ticketDetails?.FlightItinerary?.Fare?.PublishedFare);
  const promo = num(b?.applied_promo_discount);
  if (published > 0) {
    const fee = num(b?.rc_platform_fee);
    const gstRate = num(b?.rc_gst_rate);
    const feeDiscount = Math.min(fee, promo);
    const effFee = fee - feeDiscount;
    return published + effFee + (effFee * gstRate) / 100 - (promo - feeDiscount);
  }
  return num(b?.amount) - promo;
}

export function normalizeFlight(b) {
  const td = parseJson(b.ticket_details);
  const br = parseJson(b.booking_request);
  const itin =
    td?.FlightItinerary || td?.Response?.Response?.FlightItinerary || td?.Response?.FlightItinerary ||
    br?.FlightItinerary || br?.Response?.Response?.FlightItinerary || {};
  let segs = itin?.Segments || [];
  if (segs.length && Array.isArray(segs[0])) segs = segs[0];
  const first = segs[0] || {};
  const last = segs[segs.length - 1] || first;
  const paxFromCounts = num(b.adult) + num(b.children ?? b.child) + num(b.infant) + num(b.senior);
  const status = String(b.status || "").toUpperCase();
  return {
    id: b.rc_booking_id,
    type: "flight",
    status,
    bucket: bucketOf(status),
    source: first?.Origin?.Airport?.CityName || b.source_name || b.source_iata || "",
    destination: last?.Destination?.Airport?.CityName || b.destination_name || b.destination_iata || "",
    journey_date: first?.Origin?.DepTime || b.journey_date || "",
    created_at: b.created_at || "",
    pnr: b.pnr || b.booking_pnr || itin?.PNR || "",
    pax: (itin?.Passenger?.length || paxFromCounts || 0),
    amount: flightFare(b, td),
    commission: num(b.net_agent_commission),
  };
}

export function normalizeBus(b) {
  let seats = b.seat_numbers;
  if (typeof seats === "string") seats = parseJson(seats);
  const status = String(b.status || "").toUpperCase();
  return {
    id: b.rc_booking_id,
    type: "bus",
    status,
    bucket: bucketOf(status),
    source: b.source_name || "",
    destination: b.destination_name || "",
    journey_date: b.journey_date || "",
    created_at: b.created_at || "",
    pnr: b.op_pnr || b.pnr || "",
    pax: Array.isArray(seats) ? seats.length : 0,
    // Same priority as BusBookingRow: final_total_with_discounts -> amount -> total_fare
    amount: num(b.final_total_with_discounts) || num(b.amount) || num(b.total_fare),
    commission: num(b.net_agent_commission),
  };
}

// ── fetching ─────────────────────────────────────────────────────────────────
async function fetchAllPages(url, intent, extra = {}) {
  const s = getAgentSession();
  if (!s) throw new Error("Not signed in as a partner");
  const all = [];
  let lastKey = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        intent,
        From: `webbot_agent:${s.profile.email}`,
        EndUserIp: END_USER_IP,
        limit: PAGE_SIZE,
        agent_token: s.token,
        headers: { Authorization: `Bearer ${s.token}` },
        ...extra,
        ...(lastKey ? { last_evaluated_key: lastKey } : {}),
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (json?.status === 401) {
      const err = new Error(json.message || "Partner session expired");
      err.status = 401;
      throw err;
    }
    if (json?.status !== 200) throw new Error(json?.message || `Could not load ${intent}`);
    all.push(...(json.data?.bookings || []));
    lastKey = json.data?.last_evaluated_key || null;
    if (!lastKey) return { items: all, truncated: false };
  }
  return { items: all, truncated: true };
}

/**
 * Loads both histories in parallel. One failing does not hide the other:
 * returns { bookings, errors: { flight?, bus? }, truncated, unauthorized }.
 */
export async function loadAgentBookings() {
  const [flights, buses] = await Promise.allSettled([
    fetchAllPages(FLIGHT_URL, "bookinghistory", { flight_test: Boolean(FEATURES?.FLIGHT_TEST) }),
    fetchAllPages(BUS_URL, "bus_booking_history"),
  ]);
  const errors = {};
  const bookings = [];
  let truncated = false;
  if (flights.status === "fulfilled") {
    bookings.push(...flights.value.items.map(normalizeFlight));
    truncated ||= flights.value.truncated;
  } else errors.flight = flights.reason?.message || "Could not load flight bookings";
  if (buses.status === "fulfilled") {
    bookings.push(...buses.value.items.map(normalizeBus));
    truncated ||= buses.value.truncated;
  } else errors.bus = buses.reason?.message || "Could not load bus bookings";

  const unauthorized = [flights, buses].some((r) => r.status === "rejected" && r.reason?.status === 401);
  const visible = bookings
    .filter((b) => b.id && !HIDDEN.has(b.status))
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  return { bookings: visible, errors, truncated, unauthorized };
}

// ── aggregates ───────────────────────────────────────────────────────────────
const monthKey = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Commission counts only for confirmed bookings (cancelled/failed earn nothing, whatever the field says).
 * Booking value = sum of confirmed fares.
 */
export function summarize(bookings, now = new Date(), months = 6) {
  const confirmed = bookings.filter((b) => b.bucket === "confirmed");
  const thisMonth = monthKey(now);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const summary = {
    total_bookings: confirmed.length,
    bus_bookings: confirmed.filter((b) => b.type === "bus").length,
    flight_bookings: confirmed.filter((b) => b.type === "flight").length,
    total_booking_value: confirmed.reduce((s, b) => s + b.amount, 0),
    commission_earned: confirmed.reduce((s, b) => s + b.commission, 0),
    commission_this_month: confirmed.filter((b) => monthKey(b.created_at) === thisMonth).reduce((s, b) => s + b.commission, 0),
    upcoming_trips: confirmed.filter((b) => b.journey_date && new Date(b.journey_date) >= today).length,
    cancelled: bookings.filter((b) => b.bucket === "cancelled").length,
    processing: bookings.filter((b) => b.bucket === "processing").length,
  };
  summary.avg_commission = summary.total_bookings ? summary.commission_earned / summary.total_bookings : 0;

  // last N calendar months, oldest first, zero-filled
  const monthly = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = monthKey(d);
    const inMonth = confirmed.filter((b) => monthKey(b.created_at) === key);
    monthly.push({
      month: key,
      bookings: inMonth.length,
      booking_value: inMonth.reduce((s, b) => s + b.amount, 0),
      commission: inMonth.reduce((s, b) => s + b.commission, 0),
    });
  }
  return { summary, monthly };
}
